/**
 * createTimePassageSlice — Zustand slice for narrative time tracking.
 *
 * Tracks elapsed narrative time, bed-rest/medical-care toggles, and
 * broken-limb durations. The advanceTime action computes healing effects
 * via TimePassageService and applies them to the character.
 */

import { StateCreator } from 'zustand';
import { computeTimeAdvance, TimeAdvanceResult } from '../../../services/TimePassageService';

export interface BrokenLimb {
  location: string;
  weeksRemaining: number;
}

export interface TimePassageState {
  totalElapsedHours: number;
  isBedRest: boolean;
  isUnderMedicalCare: boolean;
  brokenLimbs: BrokenLimb[];
  lastAdvanceResult: TimeAdvanceResult | null;
}

export interface TimePassageSlice {
  timePassage: TimePassageState;
  setIsBedRest: (v: boolean) => void;
  setIsUnderMedicalCare: (v: boolean) => void;
  addBrokenLimb: (location: string, weeks: number) => void;
  removeBrokenLimb: (location: string) => void;
  advanceTime: (hours: number) => void;
  resetTimePassage: () => void;
}

const initialState: TimePassageState = {
  totalElapsedHours: 0,
  isBedRest: false,
  isUnderMedicalCare: false,
  brokenLimbs: [],
  lastAdvanceResult: null
};

// Uses 'any' for the combined store type to avoid circular dependency.
// The slice accesses character/healDamage/removeFatigue from the full store at runtime.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const createTimePassageSlice: StateCreator<TimePassageSlice, [], [], TimePassageSlice> = (
  set,
  get: any
) => ({
  timePassage: initialState,

  setIsBedRest: (v) => set((s) => ({ timePassage: { ...s.timePassage, isBedRest: v } })),
  setIsUnderMedicalCare: (v) =>
    set((s) => ({ timePassage: { ...s.timePassage, isUnderMedicalCare: v } })),

  addBrokenLimb: (location, weeks) =>
    set((s) => ({
      timePassage: {
        ...s.timePassage,
        brokenLimbs: [...s.timePassage.brokenLimbs, { location, weeksRemaining: weeks }]
      }
    })),

  removeBrokenLimb: (location) =>
    set((s) => ({
      timePassage: {
        ...s.timePassage,
        brokenLimbs: s.timePassage.brokenLimbs.filter((l) => l.location !== location)
      }
    })),

  advanceTime: (hours) => {
    const state = get();
    const char = state.character;
    if (!char) return;

    const progression = char.progression;
    const tb = Math.floor((char.characteristics?.toughness ?? 30) / 10);
    const tp = state.timePassage as TimePassageState;
    const totalBrokenWeeks = tp.brokenLimbs.reduce(
      (s: number, l: BrokenLimb) => s + l.weeksRemaining,
      0
    );

    const result = computeTimeAdvance(hours, {
      totalDamage: progression.woundsTracking.totalDamage,
      woundsMax: progression.woundsTracking.maximum,
      criticalDamageCount: progression.woundsTracking.criticalDamage.length,
      toughnessBonus: tb,
      currentFatigue: progression.fatigueTracking.current,
      characteristicDamage: 0,
      brokenLimbWeeksRemaining: totalBrokenWeeks,
      isBedRest: tp.isBedRest,
      isUnderMedicalCare: tp.isUnderMedicalCare
    });

    // Apply effects via other slices
    if (result.damageHealed > 0) state.healDamage(result.damageHealed);
    if (result.fatigueRemoved > 0) state.removeFatigue(result.fatigueRemoved);

    // Tick broken limbs
    const weeks = Math.floor(hours / 168);
    const updatedLimbs = tp.brokenLimbs
      .map((l: BrokenLimb) => ({ ...l, weeksRemaining: Math.max(0, l.weeksRemaining - weeks) }))
      .filter((l: BrokenLimb) => l.weeksRemaining > 0);

    set((s) => ({
      timePassage: {
        ...s.timePassage,
        totalElapsedHours: s.timePassage.totalElapsedHours + hours,
        brokenLimbs: updatedLimbs,
        lastAdvanceResult: result
      }
    }));
  },

  resetTimePassage: () => set({ timePassage: initialState })
});
